import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle, XCircle } from 'lucide-react'
import Card from '../ui/Card'
import Input from '../ui/Input'
import Textarea from '../ui/Textarea'
import Select from '../ui/Select'
import Button from '../ui/Button'

const validateEmail = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)

export default function ContactForm({ onSent }) {
    const [form, setForm] = useState({ name: '', email: '', inquiryType: '', message: '' })
    const [hp, setHp] = useState('')
    const [fieldErrors, setFieldErrors] = useState({})
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [error, setError] = useState('')
    const [charCount, setCharCount] = useState(0)

    const validateField = (name, value) => {
        const errors = { ...fieldErrors }
        if (name === 'name') {
            if (!value.trim()) errors.name = 'Name is required'
            else delete errors.name
        }
        if (name === 'email') {
            if (!value.trim()) errors.email = 'Email is required'
            else if (!validateEmail(value)) errors.email = 'Invalid email address'
            else delete errors.email
        }
        if (name === 'message') {
            if (!value.trim()) errors.message = 'Message is required'
            else delete errors.message
        }
        setFieldErrors(errors)
        return Object.keys(errors).length === 0
    }

    async function handleSubmit(e) {
        e.preventDefault()
        setError('')
        const isValid =
            validateField('name', form.name) && validateField('email', form.email) && validateField('message', form.message)
        if (!isValid) {
            setError('Please fix the errors above.')
            return
        }
        setSending(true)
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    inquiryType: form.inquiryType || 'general',
                    message: form.message.trim(),
                    hp,
                }),
            })
            const data = await res.json().catch(() => ({}))
            if (!res.ok || data?.ok === false) {
                throw new Error(data?.error || 'Server error')
            }
            setSent(true)
            setForm({ name: '', email: '', inquiryType: '', message: '' })
            setCharCount(0)
            onSent?.('Message sent successfully! Thanks for reaching out.')
            setTimeout(() => setSent(false), 5000)
        } catch (err) {
            setError('Could not send your message. Please try again.')
            onSent?.('Failed to send message')
        } finally {
            setSending(false)
        }
    }

    return (
        <Card className="p-6 sticky top-20 shadow-brutal-sm">
            <h3 className="text-xl font-serif font-bold text-black dark:text-white mb-4">Send a message</h3>

            <AnimatePresence mode="wait">
                {sent ? (
                    <Card as={motion.div} key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="!border-emerald-600 p-4 flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-green-700 dark:text-green-300">Message sent!</p>
                            <p className="text-sm text-green-600 dark:text-green-400 mt-1">Thanks for reaching out. I'll get back to you soon.</p>
                        </div>
                    </Card>
                ) : (
                    <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={handleSubmit} className="space-y-4" aria-label="Contact form">
                        <input
                            type="text"
                            name="company"
                            value={hp}
                            onChange={(e) => setHp(e.target.value)}
                            className="hidden"
                            tabIndex={-1}
                            autoComplete="off"
                        />
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                            <label htmlFor="name" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="name"
                                name="name"
                                autoComplete="name"
                                value={form.name}
                                onChange={(e) => {
                                    setForm({ ...form, name: e.target.value })
                                    validateField('name', e.target.value)
                                }}
                                onBlur={(e) => validateField('name', e.target.value)}
                                className="mt-1"
                                error={!!fieldErrors.name}
                                aria-invalid={!!fieldErrors.name}
                                aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                            />
                            {fieldErrors.name && <p id="name-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.name}</p>}
                        </motion.div>

                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                            <label htmlFor="email" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={form.email}
                                onChange={(e) => {
                                    setForm({ ...form, email: e.target.value })
                                    validateField('email', e.target.value)
                                }}
                                onBlur={(e) => validateField('email', e.target.value)}
                                className="mt-1"
                                error={!!fieldErrors.email}
                                aria-invalid={!!fieldErrors.email}
                                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                            />
                            {fieldErrors.email && <p id="email-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.email}</p>}
                        </motion.div>

                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                            <label htmlFor="inquiryType" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Inquiry Type</label>
                            <Select id="inquiryType" value={form.inquiryType} onChange={(e) => setForm({ ...form, inquiryType: e.target.value })} wrapperClassName="mt-1">
                                <option value="general">General Inquiry</option>
                                <option value="collaboration">Collaboration</option>
                                <option value="feedback">Feedback</option>
                                <option value="support">Support</option>
                                <option value="other">Other</option>
                            </Select>
                        </motion.div>

                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                            <div className="flex items-center justify-between">
                                <label htmlFor="message" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                    Message <span className="text-red-500">*</span>
                                </label>
                                <span className="text-xs text-zinc-500 dark:text-zinc-400">{charCount}/1000</span>
                            </div>
                            <Textarea
                                id="message"
                                name="message"
                                rows={5}
                                maxLength={1000}
                                value={form.message}
                                onChange={(e) => {
                                    setForm({ ...form, message: e.target.value })
                                    setCharCount(e.target.value.length)
                                    validateField('message', e.target.value)
                                }}
                                onBlur={(e) => validateField('message', e.target.value)}
                                className="mt-1 resize-none"
                                error={!!fieldErrors.message}
                                placeholder="Describe the project or topic..."
                                aria-invalid={!!fieldErrors.message}
                                aria-describedby={fieldErrors.message ? 'message-error' : undefined}
                            />
                            {fieldErrors.message && <p id="message-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.message}</p>}
                        </motion.div>

                        {error && (
                            <Card as={motion.div} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="!border-red-600 p-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                                <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                {error}
                            </Card>
                        )}

                        <Button type="submit" disabled={sending} className="w-full">
                            {sending ? 'Sending' : 'Send message'}
                        </Button>
                    </motion.form>
                )}
            </AnimatePresence>
        </Card>
    )
}
